import type { APIDeckPermissions, APISecureLink } from '~~/entities/api'
import { DeckBaseAccess, type DeckPermissionsUserRole } from '~~/entities/deck'

export function useDeckPermissions() {
  const { state: userState, hasRole } = useUser()

  async function getDeckPermissions({ deckId, companyId }: { deckId: number; companyId: number }) {
    try {
      const response = await $fetch<APIDeckPermissions>(`/v2/companies/${companyId}/decks/${deckId}/share`, {
        ...useFetchSharedOptions(),
      })
      return response
    } catch {
      return null
    }
  }

  function updateDeckPermissions({ deckId, companyId, baseAccess }: { deckId: number; companyId: number; baseAccess: string }) {
    return $fetch<APIDeckPermissions>(`/v2/companies/${companyId}/decks/${deckId}/share-base`, {
      ...useFetchSharedOptions(),
      method: 'PATCH',
      body: {
        base_access: baseAccess,
        id: deckId,
      },
    })
  }

  function upsertDeckUserPermissions({
    deckId,
    companyId,
    user_id,
    role,
  }: {
    deckId: number
    companyId: number
    user_id: number
    role: DeckPermissionsUserRole
  }) {
    return $fetch<APIDeckPermissions>(`/v2/companies/${companyId}/decks/${deckId}/share`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: {
        user_id,
        role,
      },
    })
  }

  function deleteDeckUserPermissions({ deckId, companyId, user_id }: { deckId: number; companyId: number; user_id: number }) {
    return $fetch<APIDeckPermissions>(`/v2/companies/${companyId}/decks/${deckId}/share`, {
      ...useFetchSharedOptions(),
      method: 'DELETE',
      body: {
        user_id,
      },
    })
  }

  function requestDeckSecureLink({ deckId, companyId, email }: { deckId: number; companyId: number; email: string }) {
    return $fetch<APISecureLink>(`/v2/companies/${companyId}/decks/secure_link`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: {
        deck_id: deckId,
        email,
      },
    })
  }

  function resendDeckSecureLink({ id }: { id: string }) {
    return $fetch<APISecureLink>(`/v2/decks/secure_link/${id}/resend`, {
      ...useFetchSharedOptions(),
      method: 'POST',
    })
  }

  function deleteDeckSecureLink({ id }: { id: string }) {
    return $fetch<void>(`/v2/decks/secure_link/${id}`, {
      ...useFetchSharedOptions(),
      method: 'DELETE',
    })
  }

  function userHasPermission({ permissions, companyId }: { permissions: APIDeckPermissions; companyId: number }) {
    if (hasRole('internal_admin') || permissions.base_access === DeckBaseAccess.Public) {
      return true
    } else if (permissions.base_access === DeckBaseAccess.Private) {
      return permissions.roles.some(role => role.user_id === userState.value.user?.id)
    } else if (permissions.base_access === DeckBaseAccess.CompanyView || permissions.base_access === DeckBaseAccess.CompanyEdit) {
      return userState.value.user?.companies.some(company => company.id === companyId)
    }

    return false
  }

  return {
    getDeckPermissions,
    updateDeckPermissions,
    upsertDeckUserPermissions,
    deleteDeckUserPermissions,
    requestDeckSecureLink,
    resendDeckSecureLink,
    deleteDeckSecureLink,
    userHasPermission,
  }
}
